/**
 * StepLanguageSelection Component
 *
 * Wizard step for picking the story's language and culture.
 */

import { faArrowRight } from '@fortawesome/pro-light-svg-icons/faArrowRight'
import { Box, styled, Typography } from '@mui/material'

import { Button } from '@/shared/components/Button/Button'
import { Icon } from '@/shared/components/Icon/Icon'
import { pxToRem } from '@/shared/styles/themes'

import { LanguageSelector } from '../components/LanguageSelector'

interface StepLanguageSelectionProps {
  selectedLanguageCode: string
  onLanguageChange: (languageCode: string) => void
  onContinue: () => void
  isUpdating?: boolean
}

export const StepLanguageSelection: React.FC<StepLanguageSelectionProps> = ({
  selectedLanguageCode,
  onLanguageChange,
  onContinue,
  isUpdating = false,
}) => {
  const canContinue = !!selectedLanguageCode && !isUpdating

  return (
    <StyledContainer>
      <StyledHeader>
        <Typography variant="h5" component="h2">
          Pick a Language
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose the language and cultural setting for the story
        </Typography>
      </StyledHeader>

      <LanguageSelector
        selectedLanguageCode={selectedLanguageCode}
        onSelect={onLanguageChange}
        disabled={isUpdating}
      />

      <StyledFooter>
        <Button
          primary
          size="large"
          onClick={onContinue}
          disabled={!canContinue}
          sx={{ minWidth: pxToRem(200) }}
        >
          {isUpdating ? 'Saving...' : 'Continue'}
          <Icon icon={faArrowRight} style={{ marginLeft: 8 }} />
        </Button>
      </StyledFooter>
    </StyledContainer>
  )
}

const StyledContainer = styled(Box)`
  max-width: ${pxToRem(640)};
  margin: 0 auto;
`

const StyledHeader = styled(Box)`
  text-align: center;
  margin-bottom: ${pxToRem(24)};
`

const StyledFooter = styled(Box)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-top: ${pxToRem(24)};
  border-top: 1px solid ${(props) => props.theme.palette.divider};
  margin-top: ${pxToRem(24)};
`

export default StepLanguageSelection
